// Friends page — full list + incoming/outgoing requests. Click any friend to
// open their profile OR jump straight into a DM.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { UserCheck, UserX, MessageCircle, Video, UserPlus, Users } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Friends() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ friends: [], incoming_requests: [], outgoing_requests: [] });
  const load = () => api.get("/friends").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user?.id]);

  if (!user) return <div className="p-10 text-center text-white/60">Sign in to see your friends.</div>;

  const act = async (endpoint, request_id) => {
    try { await api.post(`/friends/${endpoint}`, { request_id }); toast.success(endpoint === "accept" ? "You're now friends" : "Request declined"); load(); }
    catch { toast.error("Failed"); }
  };

  const openDm = async (otherId) => {
    try {
      const { data } = await api.post("/messenger/threads", { member_ids: [otherId] });
      nav(`/dm/${data.thread.id}`);
    } catch { toast.error("Could not open chat"); }
  };

  const call = async (otherId) => {
    try {
      const { data } = await api.post("/messenger/threads", { member_ids: [otherId] });
      await api.post("/calls/start", { thread_id: data.thread.id });
      nav(`/dm/${data.thread.id}?call=1`);
    } catch { toast.error("Could not start call"); }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="friends-page">
      <Toaster theme="dark" position="top-center" />
      <header>
        <div className="label-eyebrow text-nexus-cyan">// your circle</div>
        <h1 className="font-display font-black text-4xl mt-1">Friends</h1>
      </header>

      {data.incoming_requests.length > 0 && (
        <section className="glass p-5 border-l-2 border-nexus-mint space-y-3" data-testid="friends-incoming">
          <div className="text-[10px] tracking-widest uppercase text-nexus-mint">// incoming requests · {data.incoming_requests.length}</div>
          {data.incoming_requests.map((r) => (
            <div key={r.id} className="flex items-center gap-3" data-testid={`incoming-${r.id}`}>
              <img src={r.from_avatar} alt="" className="w-10 h-10 rounded-full" />
              <div className="flex-1 min-w-0">
                <Link to={`/u/${r.from_id}`} className="font-display font-bold text-sm hover:text-nexus-cyan">{r.from_name}</Link>
                <div className="text-[10px] text-white/40">wants to be friends</div>
              </div>
              <button onClick={() => act("accept", r.id)} className="btn-nexus text-[10px]" data-testid={`accept-${r.id}`}><UserCheck className="w-3 h-3" />Accept</button>
              <button onClick={() => act("decline", r.id)} className="btn-ghost text-[10px]" data-testid={`decline-${r.id}`}><UserX className="w-3 h-3" />Decline</button>
            </div>
          ))}
        </section>
      )}

      <section className="glass p-5 space-y-3" data-testid="friends-list">
        <div className="text-[10px] tracking-widest uppercase text-white/40">// your friends · {data.friends.length}</div>
        {data.friends.length === 0 ? (
          <div className="text-sm text-white/50">No friends yet. Visit someone&apos;s profile and hit <b>Add Friend</b>.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {data.friends.map((f) => (
              <div key={f.id} className="flex items-center gap-3 py-2" data-testid={`friend-${f.id}`}>
                <Link to={`/u/${f.id}`} className="flex items-center gap-3 flex-1 hover:opacity-90">
                  <img src={f.avatar} alt="" className="w-10 h-10 rounded-full" />
                  <div>
                    <div className="font-display font-bold text-sm">{f.display_name}</div>
                    <div className="text-[10px] text-white/40 uppercase tracking-widest">{f.role}</div>
                  </div>
                </Link>
                <button onClick={() => openDm(f.id)} title="Message" className="btn-ghost text-[10px]" data-testid={`msg-${f.id}`}><MessageCircle className="w-3.5 h-3.5" /></button>
                <button onClick={() => call(f.id)} title="Video call" className="btn-ghost text-[10px]" data-testid={`call-${f.id}`}><Video className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </section>

      {data.outgoing_requests.length > 0 && (
        <section className="glass p-5 space-y-2 opacity-70" data-testid="friends-outgoing">
          <div className="text-[10px] tracking-widest uppercase text-white/40">// pending sent · {data.outgoing_requests.length}</div>
          {data.outgoing_requests.map((r) => (
            <div key={r.id} className="flex items-center gap-3 text-xs">
              <img src={r.to_avatar} alt="" className="w-7 h-7 rounded-full" />
              <span>{r.to_name}</span>
              <span className="text-white/40">— awaiting reply</span>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
